import React from 'react';
import styles from './Contact.module.css';
import emailjs from '@emailjs/browser';

const Contact = () => {
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        project: '',
        message: ''
    });
    const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [responseMessage, setResponseMessage] = React.useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        setResponseMessage('');

        const serviceID = 'service_c1vwqjq';
        const templateID = 'template_2gb7qd6';
        const publicKey = 'z1acNND2VawM3ieBf';

        try {
            await emailjs.send(serviceID, templateID, formData, publicKey);

            setStatus('success');
            setResponseMessage("Votre message a été envoyé avec succès !");
            setFormData({ name: '', email: '', project: '', message: '' }); // Reset form
        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            setStatus('error');
            // Show a generic message but log the specific error
            setResponseMessage("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
        }
    };

    return (
        <section id="contact" className="section">
            <div className="container">
                <h2 className="section-title">Contact</h2>

                <div className={styles.contactContent}>
                    <div className={styles.contactInfo}>
                        <h3 className={styles.contactSubtitle}>Parlons de votre projet</h3>
                        <p className={styles.contactText}>
                            Vous avez un projet web en tête ? N'hésitez pas à me contacter
                            pour en discuter. Je vous répondrai dans les plus brefs délais.
                        </p>

                        <div className={styles.contactDetails}>
                            <div className={styles.contactItem}>
                                <svg className={styles.contactIcon} width="32" height="32" viewBox="0 0 32 32" fill="none">
                                    <rect x="4" y="8" width="24" height="16" rx="2" stroke="url(#contactGrad1)" strokeWidth="2" fill="none" />
                                    <path d="M4 10L16 18L28 10" stroke="url(#contactGrad1)" strokeWidth="2" strokeLinecap="round" />
                                    <defs>
                                        <linearGradient id="contactGrad1" x1="4" y1="8" x2="28" y2="24">
                                            <stop stopColor="#00ff9d" />
                                            <stop offset="1" stopColor="#2e8b57" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div>
                                    <h4>Email</h4>
                                    <a href="mailto:contact@racinetechnique.eu">contact@racinetechnique.eu</a>
                                </div>
                            </div>

                            <div className={styles.contactItem}>
                                <svg className={styles.contactIcon} width="32" height="32" viewBox="0 0 32 32" fill="none">
                                    <path d="M8 6C8 6 6 8 6 12C6 14 8 18 16 26C24 18 26 14 26 12C26 8 24 6 24 6C22 4 20 6 18 8L16 10L14 8C12 6 10 4 8 6Z" stroke="url(#contactGrad2)" strokeWidth="2" fill="none" />
                                    <defs>
                                        <linearGradient id="contactGrad2" x1="6" y1="6" x2="26" y2="26">
                                            <stop stopColor="#00ff9d" />
                                            <stop offset="1" stopColor="#2e8b57" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div>
                                    <h4>Téléphone</h4>
                                    <a href="tel:+33651684683">+33 6 51 68 46 83</a>
                                </div>
                            </div>

                            <div className={styles.contactItem}>
                                <svg className={styles.contactIcon} width="32" height="32" viewBox="0 0 32 32" fill="none">
                                    <circle cx="16" cy="14" r="4" stroke="url(#contactGrad3)" strokeWidth="2" fill="none" />
                                    <path d="M8 14C8 9 11 6 16 6C21 6 24 9 24 14C24 19 16 26 16 26C16 26 8 19 8 14Z" stroke="url(#contactGrad3)" strokeWidth="2" fill="none" />
                                    <defs>
                                        <linearGradient id="contactGrad3" x1="8" y1="6" x2="24" y2="26">
                                            <stop stopColor="#00ff9d" />
                                            <stop offset="1" stopColor="#2e8b57" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div>
                                    <h4>Localisation</h4>
                                    <p>France</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form className={`${styles.contactForm} glass`} onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label htmlFor="name">Nom *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                placeholder="Votre nom"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                required
                                placeholder="votre@email.com"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="project">Type de projet *</label>
                            <select
                                id="project"
                                name="project"
                                required
                                value={formData.project}
                                onChange={handleChange}
                            >
                                <option value="">Sélectionnez un type de projet</option>
                                <option value="creation">Création de site web</option>
                                <option value="refonte">Refonte de site</option>
                                <option value="seo">SEO / Marketing</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="autre">Autre</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="message">Message *</label>
                            <textarea
                                id="message"
                                name="message"
                                required
                                rows={6}
                                placeholder="Décrivez votre projet..."
                                value={formData.message}
                                onChange={handleChange}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary" disabled={status === 'submitting'}>
                            {status === 'submitting' ? 'Envoi en cours...' : 'Demander un devis'}
                        </button>

                        {status === 'success' && (
                            <p style={{ color: '#00ff9d', marginTop: '1rem' }}>{responseMessage}</p>
                        )}
                        {status === 'error' && (
                            <p style={{ color: '#ff4d4d', marginTop: '1rem' }}>{responseMessage}</p>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
